import { useEffect, useState } from "react";
import { ChevronDown, Disc3, ExternalLink, Loader2 } from "lucide-react";
import { api, posterUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { savedFilter } from "@/components/FilterEditor";
import { MONEY } from "@/components/WatchFilter";

const TYPE_LABEL = Object.fromEntries(MONEY.map((m) => [m.key, m.label]));

const ProviderLogo = ({ path, size = "h-7 w-7" }) =>
  path ? <img src={posterUrl(path, "w92")} alt="" className={`${size} rounded-lg object-cover shrink-0`} /> : null;

function WatchButton({ p }) {
  return (
    <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`watch-btn-${p.provider_id}`}
      className="flex items-center gap-3 h-12 px-3 rounded-2xl border bg-background font-semibold text-sm transition-[transform,border-color] hover:border-primary hover:scale-[1.01] active:scale-95">
      <ProviderLogo path={p.logo_path} />
      <span className="flex-1 min-w-0 truncate">Watch on {p.name}</span>
      <span className="text-[11px] text-muted-foreground shrink-0">{p.types.map((t) => TYPE_LABEL[t]).join(" · ")}</span>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </a>
  );
}

function PhysicalButton({ url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="watch-btn-physical"
      className="flex items-center gap-3 h-12 px-3 rounded-2xl border bg-background font-semibold text-sm transition-[transform,border-color] hover:border-primary hover:scale-[1.01] active:scale-95">
      <Disc3 className="h-6 w-6 text-muted-foreground" /> <span className="flex-1">Buy DVD / Blu-ray</span>
      <span className="text-[11px] text-muted-foreground">Amazon</span><ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
    </a>
  );
}

function MoreWays({ data }) {
  return (
    <div className="space-y-3 pt-1" data-testid="watch-more-list">
      {MONEY.map(({ key, label }) => {
        const list = data.providers.filter((p) => p.types.includes(key));
        if (!list.length) return null;
        return (
          <div key={key}>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1.5">{label}</p>
            <div className="flex flex-wrap gap-2">
              {list.map((p) => (
                <a key={p.provider_id} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`watch-more-${key}-${p.provider_id}`}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-xs font-semibold hover:border-primary">
                  <ProviderLogo path={p.logo_path} size="h-6 w-6" /> {p.name}
                </a>
              ))}
            </div>
          </div>
        );
      })}
      {data.physical && <p className="text-xs text-muted-foreground">Also released on DVD / Blu-ray.</p>}
      {data.link && (
        <a href={data.link} target="_blank" rel="noopener noreferrer" data-testid="watch-tmdb-link" className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
          See all options on TMDB <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

export function WatchOptions({ movieId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setData(null);
    setOpen(false);
    api.get(`/movies/${movieId}/watch`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [movieId]);

  if (data === false) return null;
  const w = savedFilter(user, user.active_profile || "default").watch;
  const mine = new Set(w.providers);
  const yours = data ? data.providers.filter((p) => mine.has(p.provider_id)) : [];
  const physical = data && w.physical && data.physical;
  const personal = yours.length > 0 || physical;
  const primary = personal ? yours : (data?.providers || []).slice(0, 3);

  return (
    <div className="space-y-2.5" data-testid="watch-options">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
        Where to watch{personal ? " · your services" : ""}
      </p>
      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : (
        <>
          {primary.map((p) => <WatchButton key={p.provider_id} p={p} />)}
          {physical && <PhysicalButton url={data.physical_url} />}
          {!data.providers.length && !physical && (
            <p className="text-sm text-muted-foreground" data-testid="watch-none">Not streaming, renting or selling in the US right now{data.physical ? " — but it's out on disc." : "."}</p>
          )}
          {!data.providers.length && data.physical && !physical && <PhysicalButton url={data.physical_url} />}
          {personal && !yours.length && data.providers.length > 0 && (
            <p className="text-xs text-muted-foreground">Not on your services — see other options below.</p>
          )}
          {(data.providers.length > primary.length || data.link) && (
            <button onClick={() => setOpen((o) => !o)} data-testid="watch-more-toggle"
              className="flex items-center gap-1 text-sm font-semibold text-primary">
              More ways to watch <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
          {open && <MoreWays data={data} />}
          <p className="text-[10px] text-muted-foreground">Buttons open each service's search for this title · US availability by JustWatch via TMDB</p>
        </>
      )}
    </div>
  );
}
